// Mirror subset of server/src/engine/events.ts and state.ts.
// Only the fields the receiver actually renders are declared.

export type EventType =
  | 'MATCH_CREATED'
  | 'MATCH_CONFIGURED'
  | 'PLAYER_JOINED'
  | 'PLAYER_READY'
  | 'PLAYER_LEFT'
  | 'ROUND_STARTED'
  | 'WAGER_PLACED'
  | 'PLAYER_ANSWERED'
  | 'ROUND_TIME_EXPIRED'
  | 'ROUND_REVEALED'
  | 'SCORE_UPDATED'
  | 'PLAYER_DISCONNECTED'
  | 'PLAYER_RECONNECTED'
  | 'MATCH_FINISHED';

export interface RoomEvent {
  type: EventType;
  [k: string]: unknown;
}

export interface PlayerSnapshot {
  playerId: string;
  displayName: string;
  avatarId: string;
  score: number;
  streakCount: number;
  connected: boolean;
}

export interface RoomSnapshot {
  roomId: string;
  roomCode: string;
  hostId: string;
  packId: string | null;
  players: PlayerSnapshot[];
  phase: 'lobby' | 'question' | 'reveal' | 'finished';
  currentQuestionId: string | null;
  currentRoundIndex: number;
  correctAnswerId: string | null;
  roundStartedAtMs: number | null;
  timeLimitMs: number | null;
}

export const EMPTY_SNAPSHOT: RoomSnapshot = {
  roomId: '',
  roomCode: '',
  hostId: '',
  packId: null,
  players: [],
  phase: 'lobby',
  currentQuestionId: null,
  currentRoundIndex: 0,
  correctAnswerId: null,
  roundStartedAtMs: null,
  timeLimitMs: null,
};

// ─── Reducer ─────────────────────────────────────────────────────────────

export function reduce(state: RoomSnapshot, event: RoomEvent): RoomSnapshot {
  switch (event.type) {
    case 'MATCH_CREATED':
      return {
        ...state,
        roomId: (event['matchId'] as string) ?? state.roomId,
        packId: (event['packId'] as string | undefined) ?? state.packId,
        hostId: (event['hostId']  as string | undefined) ?? state.hostId,
      };

    case 'PLAYER_JOINED': {
      const pid  = event['playerId']    as string;
      const name = event['displayName'] as string;
      const av   = (event['avatarId']   as string) ?? 'default';
      if (state.players.some((p) => p.playerId === pid)) return state;
      return {
        ...state,
        players: [...state.players, {
          playerId: pid, displayName: name, avatarId: av,
          score: 0, streakCount: 0, connected: true,
        }],
      };
    }

    case 'PLAYER_LEFT': {
      const pid = event['playerId'] as string;
      return {
        ...state,
        players: state.players.map((p) =>
          p.playerId === pid ? { ...p, connected: false } : p),
      };
    }

    case 'PLAYER_DISCONNECTED':
    case 'PLAYER_RECONNECTED': {
      const pid       = event['playerId'] as string;
      const connected = event.type === 'PLAYER_RECONNECTED';
      return {
        ...state,
        players: state.players.map((p) =>
          p.playerId === pid ? { ...p, connected } : p),
      };
    }

    case 'ROUND_STARTED':
      return {
        ...state,
        phase: 'question',
        currentQuestionId: event['questionId'] as string,
        currentRoundIndex: event['roundIndex'] as number,
        roundStartedAtMs:  event['startedAt']  as number,
        timeLimitMs:       event['timeLimitMs'] as number,
        correctAnswerId:   null,
      };

    case 'ROUND_REVEALED': {
      const scoring = (event['perPlayerScoring'] as Record<string, { total: number }>) ?? {};
      return {
        ...state,
        phase: 'reveal',
        correctAnswerId: event['correctAnswerId'] as string,
        players: state.players.map((p) => ({
          ...p,
          score: p.score + (scoring[p.playerId]?.total ?? 0),
        })),
      };
    }

    case 'SCORE_UPDATED': {
      const scores = (event['scores'] as Record<string, number>) ?? {};
      return {
        ...state,
        players: state.players.map((p) =>
          p.playerId in scores ? { ...p, score: scores[p.playerId]! } : p),
      };
    }

    case 'MATCH_FINISHED':
      return { ...state, phase: 'finished' };

    default:
      return state;
  }
}
